import { DatePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LearningPathPersonByStateModel } from 'src/app/models/person-states/learning-path-person-by-state.model';
import { TableColumn } from 'src/app/models/table-column.model';
import { LearningPathService } from 'src/app/services/learning-path.service';
import { LoadingService } from 'src/app/services/loading.service';

export type PersonTab =
  'completedPersons' | 'startedPersons' | 'openPersons';

export interface PersonTableRowModel {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  locationName?: string;
  date?: Date;            
  raw?: unknown;          
}

@Component({
  selector: 'app-learning-path-persons-table',
  standalone: false,
  templateUrl: './learning-path-persons-table.component.html',
  styleUrl: './learning-path-persons-table.component.scss',
})
export class LearningPathPersonsTableComponent implements OnInit {
  @Input() learningPathId!: string;

  selectedTab!: PersonTab;
  
  columns: TableColumn[] = [];
  persons: PersonTableRowModel[] = [];
  
  private personsByState!: LearningPathPersonByStateModel;
  
  personsTabs: { id: number; text: string; key: PersonTab }[] = [
    {
      id: 0,
      text: this.translateService.instant(
        'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.CLOSED'
      ),
      key: 'completedPersons',
    },
    {
      id: 1,
      text: this.translateService.instant(
        'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.IN-PROGRESS'
      ),
      key: 'startedPersons',
    },
    {
      id: 2,
      text: this.translateService.instant(
        'ADMINISTRATION.PROCESSING-PROGRESS-STATUSES.OPEN'
      ),
      key: 'openPersons'
    }
  ];

  constructor(
    private learningPathService: LearningPathService,
    private loadingService: LoadingService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.selectedTab = this.personsTabs[0].key;
    this.loadPersons();
  }

  public loadPersons(): void {
    this.loadingService
      .load(this.learningPathService.getLearningPathPersons(this.learningPathId))
      .subscribe((personsByState) => {
        this.personsByState = personsByState;
        this.applyTab(this.selectedTab);
      });
  }

  onTabChange(tab: PersonTab): void {
    this.selectedTab = tab;
    this.applyTab(tab);
  }

  private applyTab(tab: PersonTab): void {
    this.persons = this.mapPersons(tab, this.personsByState);

    switch (tab) {
      case 'completedPersons':
        this.columns = [
          ...this.baseColumns,
          this.createDateColumn('COMMON.FIELDS.COMPLETED-DATE'),
        ];
        break;

      case 'startedPersons':
        this.columns = [
          ...this.baseColumns,
          this.createDateColumn('COMMON.FIELDS.STARTED-DATE')
        ];
        break;

      case 'openPersons':
        this.columns = [...this.baseColumns];
        break;
    }
  }

  private mapPersons(tab: PersonTab, data: LearningPathPersonByStateModel): PersonTableRowModel[] {
    switch (tab) {
      case 'completedPersons':
        return data.completedPersons.map((p) => ({
          id: p.person.id,
          lastName: p.person.lastName,
          firstName: p.person.firstName,
          email: p.person.email,
          locationName: p.person.location?.name,
          date: p.completedAt,
          raw: p,
        }));

      case 'startedPersons':
        return data.startedPersons.map((p) => ({
          id: p.person.id,
          lastName: p.person.lastName,
          firstName: p.person.firstName,
          email: p.person.email,
          locationName: p.person.location?.name,
          date: p.startedAt,
          raw: p,
        }));

      case 'openPersons':
        return data.openPersons.map((p) => ({
          id: p.id,
          lastName: p.lastName,
          firstName: p.firstName,
          email: p.email,
          locationName: p.location?.name,
          raw: p,
        }));

      default:
        return [];
    }
  }

  private readonly baseColumns: TableColumn[] = [
    {
      dataField: 'lastName',
      caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
      cssClass: 'width-200',
    },
    {
      dataField: 'firstName',
      caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
      cssClass: 'width-200',
    },
    {
      dataField: 'email',
      caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
      cssClass: 'width-150',
    },
    {
      dataField: 'locationName',
      caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
      cssClass: 'width-150',
    },
  ];

  private createDateColumn(translationKey: string): TableColumn {
    return {
      dataField: 'date',
      caption: this.translateService.instant(translationKey),
      cellValue: (data: any): any => {
        return new Date(data.date);
      },
      calculateSortValue: (data: PersonTableRowModel): Date | string => {
        if (!data.date) {
          return '';
        }

        return new Date(data.date);
      },
      calculateDisplayValue: (row: PersonTableRowModel): string => {
          if (!row.date) {
          return '';
        }

        return (
          new DatePipe(this.translateService.currentLang).transform(
            new Date(row.date),
            'dd.MM.yyyy'
          ) || ''
        );
      },
      cssClass: 'width-150',
    };
  }
}
